import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import { FaArrowRight, FaBolt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const Features = () => {
  const navigate = useNavigate();

  // ✅ All PowerSense Blogs
  const features = [
    {
      id: 1,
      title: "Campus Energy Monitoring System (CEMS)",
      description: "Monitor complete campus energy usage in real-time.",
      image: "https://images.unsplash.com/photo-1509395176047-4a66953fd231",
      readTime: "6 min read",
      category: "CEMS"
    },
    {
      id: 2,
      title: "UPS Monitoring System",
      description: "Track UPS performance, battery, and load in real-time.",
      image: "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc",
      readTime: "5 min read",
      category: "UPS"
    },
    {
      id: 3,
      title: "Solar Monitoring System",
      description: "Monitor solar power generation and efficiency.",
      image: "https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9",
      readTime: "5 min read",
      category: "Solar"
    },
    {
      id: 4,
      title: "Real-Time Energy Dashboard",
      description: "Visualize energy data with live dashboards and widgets.",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31",
      readTime: "7 min read",
      category: "Dashboard"
    },
    {
      id: 5,
      title: "Training & Courses Available",
      description: "Learn IoT, energy monitoring, and PowerSense tools.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f",
      readTime: "4 min read",
      category: "Training"
    },
    {
      id: 6,
      title: "Agriculture Monitoring System",
      description: "Smart farming with IoT sensors for soil and water.",
      image: "https://images.unsplash.com/photo-1501004318641-b39e6451bec6",
      readTime: "6 min read",
      category: "Agriculture"
    },
    {
      id: 7,
      title: "Diesel Generator Monitoring",
      description: "Track fuel usage and generator performance.",
      image: "https://images.unsplash.com/photo-1581091870622-1e7c7a1c6d34",
      readTime: "5 min read",
      category: "DG System"
    },
    {
      id: 8,
      title: "Water Monitoring System",
      description: "Monitor water usage and tank levels in real-time.",
      image: "https://images.unsplash.com/photo-1506744038136-46273834b3fb",
      readTime: "5 min read",
      category: "Water"
    }
  ];

  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => setIsVisible(true), []);

  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % features.length);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, features.length]);

  const handleSlide = (dir) => {
    setIsAutoPlaying(false);
    if (dir === 'right') {
      setCurrentIndex((prev) => (prev + 1) % features.length);
    } else {
      setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
    }
    setTimeout(() => setIsAutoPlaying(true), 4000);
  };

  const handleCardClick = (id) => navigate(`/blog/${id}`);
  const handleGetStarted = () => navigate('/enquiry');

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-blue-50 via-green-50 to-cyan-50 py-16 overflow-hidden">

      {/* Background Glow */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-blue-300/30 blur-3xl rounded-full"></div>
        <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-green-300/30 blur-3xl rounded-full"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">

        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            className="text-4xl md:text-5xl font-bold text-gray-800"
          >
            PowerSense{" "}
            <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              Blogs
            </span>
          </motion.h2>

          <p className="text-gray-600 mt-4 text-lg">
            Explore IoT, UPS monitoring, and energy analytics insights
          </p>
        </div>

        {/* MAIN CARD */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl"
          >
            <div className="relative h-[450px]">
              <img 
                src={features[currentIndex].image} 
                alt={features[currentIndex].title}
                className="w-full h-full object-cover" 
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent" />

              <div className="absolute top-4 left-4 flex gap-2">
                <span className="bg-gradient-to-r from-blue-500 to-green-500 px-3 py-1 text-white rounded-full text-sm">
                  {features[currentIndex].category}
                </span>
                <span className="bg-white/20 backdrop-blur-md px-3 py-1 text-sm rounded-full text-white border border-white/30">
                  {features[currentIndex].readTime}
                </span>
              </div>

              <div className="absolute bottom-0 p-8 text-white">
                <h3 className="text-3xl font-bold mb-3 flex items-center gap-2">
                  <FaBolt className="text-yellow-400" />
                  {features[currentIndex].title}
                </h3>

                <p className="mb-4 text-gray-200">
                  {features[currentIndex].description}
                </p>

                <button
                  onClick={() => handleCardClick(features[currentIndex].id)}
                  className="bg-gradient-to-r from-blue-500 via-cyan-500 to-green-500 px-6 py-3 rounded-full flex items-center gap-2 shadow-lg hover:scale-105 transition"
                >
                  Read Article <FaArrowRight />
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Arrows */}
        <button onClick={() => handleSlide('left')} className="absolute left-5 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-md p-3 rounded-full shadow">
          <IoChevronBackOutline />
        </button>

        <button onClick={() => handleSlide('right')} className="absolute right-5 top-1/2 -translate-y-1/2 bg-white/80 backdrop-blur-md p-3 rounded-full shadow">
          <IoChevronForwardOutline />
        </button>

        {/* SMALL CARDS */}
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {features.map((f) => (
            <div 
              key={f.id} 
              onClick={() => handleCardClick(f.id)}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl shadow-lg hover:scale-105 hover:shadow-2xl transition cursor-pointer"
            >
              <img 
                src={f.image} 
                alt={f.title}
                className="h-40 w-full object-cover" 
              />

              <div className="p-4">
                <h3 className="font-bold text-gray-800 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600">{f.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 to-green-600 text-white p-10 rounded-2xl text-center">
          <h2 className="text-3xl font-bold mb-4">Start Monitoring Your Energy Today</h2>
          <p className="mb-6">Get real-time insights with PowerSense</p>
          <button 
            onClick={handleGetStarted} 
            className="bg-white text-blue-600 px-6 py-3 rounded-full shadow hover:scale-105 transition"
          >
            Get Started <FaArrowRight className="inline ml-2" />
          </button>
        </div>

      </div>
    </section>
  );
};

export default Features;